"use client";

import { useAtom, useAtomValue } from "jotai";
import { z } from "zod";
import { LuReply } from "react-icons/lu";
import { useState, useEffect, Dispatch, SetStateAction, useRef } from "react";
import { PiCaretDownBold } from "react-icons/pi";
import { Title } from "@/components/ui/text";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/ui/avatar";
import {
  dataAtom,
  messageIdAtom,
} from "@/app/shared/support/inbox/message-list";
import { SubmitHandler, Controller } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Empty, EmptyBoxIcon } from "@/components/ui/empty";
import ActionDropdown from "@/app/shared/support/inbox/action-dropdown";
import Select from "@/components/ui/select";
import MessageBody from "@/app/shared/support/inbox/message-body";
import cn from "@/utils/class-names";
import SimpleBar from "@/components/ui/simplebar";
import { useElementSize } from "@/hooks/use-element-size";
import { useMedia } from "@/hooks/use-media";
import dynamic from "next/dynamic";
import { SupportType, supportTypes } from "@/data/support-inbox";
import { Input } from "@/components/ui/input";
import Spinner from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import useFetch from "@/hooks/fetch/useFetch";
import getData from "@/functions/getData";
import { Axios } from "axios";
import { apis } from "@/static/apis/apis";
import { Chatroom, Message } from "@/types/chatroom/chatroom";
import { useSession } from "next-auth/react";
import avatarAddress from "@/functions/avatarAddress";
import postData from "@/functions/postData";
import useAxiosAuth from "@/hooks/fetch/useAxiosAuth";
import { produce } from "immer";
import { GoPaperclip } from "react-icons/go";
const QuillEditor = dynamic(() => import("@/components/ui/quill-editor"), {
  ssr: false,
});
import { IoMdSend } from "react-icons/io";
import UploadFile from "@/components/modal/upload-file";
import { chatInput } from "@/store/chat";
import { motion } from "framer-motion";
import FallingBoard from "./FallingBoard";

const FormSchema = z.object({
  message: z.string(),
});

type FormValues = {
  message: string;
};

const contactStatuses = [
  {
    value: "New",
    name: "جدید",
  },
  {
    value: "Waiting on contact",
    name: "در انتظار مخاطب",
  },
  {
    value: "Waiting on us",
    name: "در حال انتظار",
  },
  {
    value: "Closed",
    name: "بسته",
  },
];

const supportOptionTypes = [
  {
    value: supportTypes.Chat,
    name: supportTypes.Chat,
  },
  {
    value: supportTypes.Email,
    name: supportTypes.Email,
  },
];

export default function MessageDetails({
  className,
  id,
}: {
  className?: string;
  id: number;
}) {
  const data = useAtomValue(dataAtom);
  const [agent, setAgent] = useState();
  const [priority, setPriority] = useState("");
  const messageId = useAtomValue(messageIdAtom);
  const [isLoading, setIsLoading] = useState(false);
  const [supportType, setSupportType] = useState<SupportType>();
  const [contactStatus, setContactStatus] = useState(contactStatuses[0]?.name);
  const [ref, { width }] = useElementSize();
  const isWide = useMedia("(min-width: 1280px) and (max-width: 1440px)", false);
  const [reset, setReset] = useState({ message: "" });
  function formWidth() {
    if (isWide) return width - 64;
    return width - 44;
  }

  const message = data.find((m) => m.id === messageId) ?? data[0];
  const initials = `${message?.firstName.charAt(0)}${message?.lastName.charAt(
    0
  )}`;
  const [chatIn, setChatIn] = useAtom(chatInput);
  const { data: session } = useSession();
  const userId = session?.user.userId;
  const [uploadModal, setUploadModal] = useState(false);
  let admin = session?.user.user_permissions.chatroom_watcher;
  const [messages, setMessages] = useFetch(
    (axios: Axios, setData: Dispatch<any>) =>
      getData(
        admin
          ? apis.chatroom.adminMessageList(id)
          : apis.chatroom.messageList(id),
        axios,
        setData
      )
  ) as [
    messages: { data: Chatroom | undefined; loading: boolean; error: any },
    setMessage: Dispatch<
      SetStateAction<{
        data: Chatroom | undefined;
        loading: boolean;
        error: any;
      }>
    >,
  ];
  useEffect(() => {
    const timer = () => {
      if (id >= 0)
        getData(
          admin
            ? apis.chatroom.adminMessageList(id)
            : apis.chatroom.messageList(id),
          axios,
          setMessages as Dispatch<any>
        );
    }; // 500 milliseconds
    const interval = setInterval(timer, 1 * 10 * 1000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    setSupportType(message?.supportType);
  }, [message]);
  const axios = useAxiosAuth();
  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    setIsLoading(true);
    let result = await postData(
      apis.message.create,
      axios,
      {
        chatroom: id,
        content: chatIn,
      },
      undefined,
      true,
      () => {
        setChatIn("");
        // setMessages((x) =>
        //   produce(x, (draftState) => {
        //     draftState.data?.messages.unshift({
        //       id: result.id,
        //       owner: result.owner.id,
        //       chatroom: id,
        //       is_seen: false,
        //       content: result.content,
        //       create_date: result.create_date,
        //     } as Message);
        //   })
        // );
      }
    );
    setReset({ message: "" });
    setIsLoading(false);
  };

  if (messages.loading) {
    return (
      <div
        className={cn(
          "!grid h-full min-h-[128px] flex-grow place-content-center items-center justify-center",
          className
        )}
      >
        <Spinner size="xl" />
      </div>
    );
  }

  if (!messages.data) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={cn(
          "!grid h-full min-h-[128px] flex-grow place-content-center items-center justify-center",
          className
        )}
      >
        <Empty
          text="No conversations selected"
          textClassName="mt-4 text-base text-gray-500"
        />
      </motion.div>
    );
  }
  if (!messages.loading)
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className={cn(
          "relative pt-6 rounded-lg border border-gray-200 px-4  py-7 xl:px-5 xl:py-5 2xl:pb-7 2xl:pt-6 h-fit",
          className
        )}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          className="absolute top-[-1px] left-[60px]"
        >
          <FallingBoard />
        </div>
        <div className="flex flex-col h-full ">
          <div
            className={
              "max-h-[calc(100dvh-10rem)] @3xl:max-h-[calc(100dvh-10rem)] @4xl:max-h-[calc(100dvh-10rem)] @7xl:max-h-[calc(100dvh-10rem)] overflow-auto flex flex-col-reverse scrollbar-thumb-gray-300 scrollbar-track-gray-0 h-[calc(100dvh-20rem)] scrollbar-thin" +
              (messages.data?.messages && messages.data?.messages.length > 0
                ? ""
                : " justify-center")
            }
          >
            {messages.data?.messages && messages.data?.messages.length > 0 ? (
              messages.data?.messages.map((message, index) => {
                if (
                  messages.data?.owner &&
                  message.owner === messages.data?.owner.id
                ) {
                  return (
                    <MessageBody
                      key={index}
                      messageData={message}
                      userData={messages.data?.owner}
                    />
                  );
                } else if (messages.data?.owner)
                  return (
                    <MessageBody
                      key={index}
                      messageData={message}
                      userData={messages.data?.target}
                    />
                  );
              })
            ) : (
              <Empty text="هنوز پیامی وجود ندارد" textClassName="mt-2" />
            )}
          </div>
          <div
            ref={ref}
            className="grid grid-cols-[32px_1fr] items-start gap-3 rounded-b-lg  @3xl:pt-4 dark:bg-transparent lg:gap-4 lg:pl-0 dark:lg:pt-0 xl:grid-cols-[48px_1fr]"
          >
            <figure className="dark:mt-4">
              <Avatar
                name="John Doe"
                initials={initials}
                src={avatarAddress(userId)}
                className="!h-8 !w-8 bg-[#70C5E0] font-medium text-white xl:!h-12 xl:!w-12"
              />
            </figure>
            <div
              className="relative rounded-lg border border-gray-200 bg-gray-50 p-4 2xl:p-5"
              style={{
                maxWidth: formWidth(),
              }}
            >
              <Form<FormValues>
                onSubmit={onSubmit}
                validationSchema={FormSchema}
                resetValues={reset}
              >
                {({ register, control, watch, formState: { errors } }) => {
                  return (
                    <>
                      <div className="relative mb-2.5 flex ">
                        <div className="flex flex-col">
                          <Button
                            variant="outline"
                            size="sm"
                            className="dark:bg-gray-200 dark:text-white mx-1 "
                            onClick={() => {
                              setUploadModal((x) => !x);
                            }}
                          >
                            <GoPaperclip className="w-5 h-5" />
                          </Button>
                          <Button
                            type="submit"
                            size="sm"
                            className="dark:bg-gray-200 dark:text-white m-1"
                            isLoading={isLoading}
                            disabled={chatIn === ""}
                          >
                            <IoMdSend className="w-5 h-5" />
                          </Button>
                        </div>
                        <Textarea
                          size={"sm"}
                          rows={4}
                          value={chatIn}
                          placeholder="پیام خود را اینجا وارد کنید"
                          className="[&>label>span]:font-medium grow mx-2 "
                          {...register("message")}
                          error={errors.message?.message}
                          onChange={(e) => {
                            setChatIn(e.target.value);
                          }}
                        />
                      </div>
                    </>
                  );
                }}
              </Form>
            </div>
          </div>
        </div>
        <UploadFile
          onClose={() => {
            setUploadModal((x) => !x);
          }}
          isOpen={uploadModal}
          id={id}
        />
      </motion.div>
    );
}

export function DotSeparator({ ...props }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="4"
      height="4"
      viewBox="0 0 4 4"
      fill="none"
      {...props}
    >
      <circle cx="2" cy="2" r="2" fill="#D9D9D9" />
    </svg>
  );
}
